import { useState, useEffect, useRef } from "react";
import { SPORTSBOOKS, SUPPORTED_LEAGUES } from "../../lib/constants";
import { leagueLabel } from "../../lib/league-display";
import { readLastLeague, writeLastLeague } from "../../lib/league-preference";
import BottomSheet from "../../components/BottomSheet";
import OfflineBanner from "../../components/ui/OfflineBanner";
import SportsbookSelector from "./components/SportsbookSelector";
import ImportSignInGate from "../import/components/ImportSignInGate";
import SlipImporter from "../import/SlipImporter";
import { accessDeniedMessage } from "../../lib/accessCopy";

export default function AddSlipSheet({open,onAdd,onClose,games,rosters,rostersLoaded,user,onSignIn,isOnline,isReady,syncFailed,retrySync,betaGate}) {
  const { allowed: betaAllowed, loading: betaLoading, error: betaError, status: betaStatus, refetch: betaRefetch } = betaGate || {};
  const [sportsbook,setSportsbook]=useState("fd");
  const [sheetLocked,setSheetLocked]=useState(false);
  const [offlineMsg, setOfflineMsg] = useState(false);
  // Entry league: seeded from the remembered pick, reset to it on close (so a
  // newer value written elsewhere wins on the next open). No league literal here --
  // the registry owns the default (league-preference.js).
  const [league, setLeague] = useState(() => readLastLeague());
  useEffect(() => { if (!open) setLeague(readLastLeague()); }, [open]);
  // A denied access answer is cached for the session. Opening the sheet on any denial re-checks once per open (a
  // ref, not the status, so a refetch that cycles the status cannot loop), so an approval shows without an app
  // restart (#1503 pre-Codex FC-2; first added for consent_missing in #1502 pre-Codex FC F1).
  const recheckedRef = useRef(false);
  useEffect(() => {
    if (!open) { recheckedRef.current = false; return; }
    if (recheckedRef.current || betaAllowed !== false || !betaRefetch) return;
    recheckedRef.current = true;
    betaRefetch();
  }, [open, betaAllowed, betaRefetch]);
  const pickLeague = (l) => { setLeague(l); writeLastLeague(l); };
  useEffect(() => {
    if (!offlineMsg) return;
    const t = setTimeout(() => setOfflineMsg(false), 3000);
    return () => clearTimeout(t);
  }, [offlineMsg]);

  const lbl = {fontSize:10,color:"#334155",marginBottom:5,display:"block",letterSpacing:"1px",textTransform:"uppercase"};

  return (
    <BottomSheet open={open} onClose={onClose} locked={sheetLocked}>
      <div style={{padding:"8px 18px 48px"}}>
        <OfflineBanner visible={offlineMsg} onDismiss={()=>setOfflineMsg(false)}/>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
          <span style={{fontSize:17,fontWeight:800,color:"#f1f5f9",letterSpacing:"-0.3px"}}>Import a Slip</span>
          <button onClick={sheetLocked ? undefined : onClose} style={{background:"none",border:"none",color:"#475569",fontSize:20,cursor:"pointer"}}>✕</button>
        </div>

        {/* League */}
        <div style={{marginBottom:14}}>
          <span style={lbl}>League</span>
          <div style={{display:"flex",gap:8}}>
            {SUPPORTED_LEAGUES.map((l) => {
              const on = league === l;
              return (
                <button key={l} onClick={sheetLocked?undefined:()=>pickLeague(l)} disabled={sheetLocked} data-testid={`league-${l}`} style={{flex:1,padding:"8px 12px",borderRadius:8,border:`1px solid ${on?'#4f46e5':'#1e293b'}`,background:on?'#1e1b4b':'transparent',color:on?'#a5b4fc':'#475569',fontSize:13,fontWeight:700,cursor:sheetLocked?"not-allowed":"pointer",opacity:sheetLocked?0.5:1,fontFamily:"inherit"}}>{leagueLabel(l)}</button>
              );
            })}
          </div>
        </div>

        {/* Sportsbook */}
        <div style={{marginBottom:14}}>
          <SportsbookSelector sportsbook={sportsbook} onChange={setSportsbook} labelStyle={lbl}/>
        </div>

        {/* IMPORT */}
        {!user ? (
          <ImportSignInGate onSignIn={onSignIn}/>
        ) : betaLoading || (betaAllowed === null && !betaError) ? (
          <div style={{textAlign:"center",padding:"40px 0",color:"#475569",fontSize:13}}>Checking beta access...</div>
        ) : betaError ? (
          <div style={{textAlign:"center",padding:"40px 0"}}>
            <div style={{color:"#f59e0b",fontSize:13,marginBottom:12}}>We couldn't verify beta access right now. Check your connection and try again.</div>
            <button onClick={betaRefetch} style={{padding:"10px 24px",background:"none",border:"1px solid #f59e0b",borderRadius:10,color:"#f59e0b",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Retry access check</button>
          </div>
        ) : betaAllowed === false ? (
          <div style={{textAlign:"center",padding:"40px 0",color:"#94a3b8",fontSize:13,lineHeight:1.6}}>
            {accessDeniedMessage(betaStatus)}
            {(betaStatus === "consent_missing" || betaStatus === "not_found") && betaRefetch && (
              <div style={{marginTop:12}}>
                <button onClick={betaRefetch} style={{minHeight:44,padding:"10px 24px",background:"none",border:"1px solid #94a3b8",borderRadius:10,color:"#94a3b8",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Check again</button>
              </div>
            )}
          </div>
        ) : (
          <SlipImporter
            games={games}
            sportsbook={sportsbook}
            league={league}
            onConfirm={async (bet, closeAfter=true)=>{await onAdd(bet);if(closeAfter)onClose();}}
            user={user}
            rosters={rosters}
            rostersLoaded={rostersLoaded}
            open={open}
            isOnline={isOnline}
            onLockChange={setSheetLocked}
            isReady={isReady}
            syncFailed={syncFailed}
            retrySync={retrySync}
            refetchBetaStatus={betaRefetch}
          />
        )}
      </div>
    </BottomSheet>
  );
}
